/// <amd-module name="Reporting/BoardsCommon/ReportGuidedTourUtility" />
import * as React from "react";
import { publishTeachingBubbleTelemetry, TeachingBubbleTelemetryProps } from "Reporting/BoardsCommon/ReportTelemetry";
import * as Resources from "Reporting/BoardsCommon/Resources";
import { ObservableValue } from "VSS/Core/Observable";
import { format } from "VSS/Core/Util/String";
import { IVssPageContext } from "VSS/Platform/Context";
import { ISettingsService, SettingsUserScope } from "VSS/Platform/Settings";
import { Button } from "VSSUI/Button";
import { Observer } from "VSSUI/Observer";
import { TeachingBubble } from "VSSUI/TeachingBubble";
import { Location } from "VSSUI/Utilities/Position";

export interface ReportBubbleProps {
    title?: string;
    text?: string;
}

/*
 * This is used to pass the text to be displayed in the respective teaching bubbles to the common implementation
 */
export interface ReportGuidedTourUtilityProps {
    reportKey: string;
    tourBubble?: ReportBubbleProps;
    filterBubble?: ReportBubbleProps;
    chartBubble?: ReportBubbleProps;
    legendBubble?: ReportBubbleProps;
    reportDocumentationUrl?: string;
}

/*
 * This class manages an instance of the guided tour teaching bubbles for all reports
 * The tour is triggered on the first day by default and also on demand when the user clicks the info icon
 * The collection level helps track if user has seen the tour before for default loading scenario
 */
export class ReportGuidedTourUtility {
    public static teachingBubbleSeenKey: string = "ReportTourSeen";

    private tutorialDismissed = -1;
    // Stores the array of teaching bubbles
    private teachingBubbleTourProps: any[] = [];
    // Stores the index of current active bubble
    private activeTourStep = new ObservableValue<number>(this.tutorialDismissed);

    constructor(private pageContext: IVssPageContext, private tourUtilityProps: ReportGuidedTourUtilityProps, private dismissBubble: () => void) {}

    public titleBubbleCallback = (element: HTMLDivElement) => {
        this.teachingBubbleTourProps.push({
            anchorElement: element,
            anchorOrigin: { horizontal: Location.center, vertical: Location.end },
            primaryButtonProps: this.getLinkButton(),
            secondaryButtonProps: this.getNextButton(),
            title: this.tourUtilityProps.tourBubble!.title,
            text: this.tourUtilityProps.tourBubble!.text
        });
        this.activeTourStep.value = 0;
    };

    public filterBubbleCallback = (element: HTMLDivElement) => {
        this.teachingBubbleTourProps.push({
            anchorElement: element,
            anchorOrigin: { horizontal: Location.end, vertical: Location.end },
            primaryButtonProps: this.getPreviousButton(),
            secondaryButtonProps: this.getNextButton(),
            title: this.tourUtilityProps.filterBubble!.title,
            text: this.tourUtilityProps.filterBubble!.text
        });
    };

    public chartBubbleCallback = (element: HTMLDivElement) => {
        this.teachingBubbleTourProps.splice(1, 0, {
            anchorElement: element,
            anchorOrigin: { horizontal: Location.start, vertical: Location.start },
            primaryButtonProps: this.getPreviousButton(),
            secondaryButtonProps: this.getNextButton(),
            title: this.tourUtilityProps.chartBubble!.title,
            text: this.tourUtilityProps.chartBubble!.text
        });
        this.teachingBubbleTourProps.push({
            anchorElement: element,
            anchorOrigin: { horizontal: Location.start, vertical: Location.end },
            primaryButtonProps: this.getPreviousButton(),
            secondaryButtonProps: this.getDismissButton(),
            title: this.tourUtilityProps.legendBubble!.title,
            text: this.tourUtilityProps.legendBubble!.text
        });
    };

    public render(showTeachingBubble?: boolean): JSX.Element {
        return (
            <>
                {showTeachingBubble && (
                    <Observer activeBubble={this.activeTourStep}>
                        {(props: { activeBubble: number }) => {
                            return props.activeBubble !== this.tutorialDismissed ? (
                                <TeachingBubble {...this.teachingBubbleTourProps[props.activeBubble]} onDismiss={this.dismissTour} />
                            ) : null;
                        }}
                    </Observer>
                )}
            </>
        );
    }

    private dismissTour = () => {
        this.dismissBubble();
        publishTeachingBubbleTelemetry(
            this.pageContext,
            this.tourUtilityProps.reportKey,
            this.getTelemetryProps(this.activeTourStep.value, "Dismiss")
        );
        this.activeTourStep.value = this.tutorialDismissed;
        saveUserBubbleSettings(this.pageContext, this.tourUtilityProps.reportKey);
        this.render(false);
    };

    private nextBubble = () => {
        if (this.activeTourStep.value < this.teachingBubbleTourProps.length - 1) {
            publishTeachingBubbleTelemetry(
                this.pageContext,
                this.tourUtilityProps.reportKey,
                this.getTelemetryProps(this.activeTourStep.value, "Next")
            );
            this.activeTourStep.value++;
            this.render(true);
        }
    };

    private previousBubble = () => {
        if (this.activeTourStep.value > 0) {
            publishTeachingBubbleTelemetry(
                this.pageContext,
                this.tourUtilityProps.reportKey,
                this.getTelemetryProps(this.activeTourStep.value, "Previous")
            );
            this.activeTourStep.value--;
            this.render(true);
        }
    };

    private getLinkButton() {
        return {
            text: Resources.TeachingBubbleLearnMoreButtonText,
            target: "_blank",
            href: this.tourUtilityProps.reportDocumentationUrl,
            onClick: this.publishLearnMoreClickTelemetry,
            role: "link",
            ariaLabel: format(Resources.TeachingBubbleLearnMoreButtonDescriptiveText, this.tourUtilityProps.tourBubble!.title)
        };
    }

    private getDismissButton() {
        return {
            text: Resources.TeachingBubbleEndTourButtonText,
            primary: true,
            onClick: this.dismissTour
        };
    }

    private getNextButton() {
        return {
            text: Resources.TeachingBubbleNextButtonText,
            primary: true,
            onClick: this.nextBubble
        };
    }

    private getPreviousButton() {
        return {
            text: Resources.TeachingBubblePreviousButtonText,
            onClick: this.previousBubble
        };
    }

    private publishLearnMoreClickTelemetry = () => {
        publishTeachingBubbleTelemetry(
            this.pageContext,
            this.tourUtilityProps.reportKey,
            this.getTelemetryProps(this.activeTourStep.value, "LearnMore")
        );
    };

    private getTelemetryProps(currentBubbleValue: number, action: string): TeachingBubbleTelemetryProps {
        return {
            currentTeachingBubbleName: TeachingBubbleList[currentBubbleValue],
            buttonAction: action
        };
    }
}

enum TeachingBubbleList {
    Dismiss = -1,
    IntroBubble,
    ChartBubble,
    FilterBubble,
    LegendBubble
}

export async function saveUserBubbleSettings(pageContext: IVssPageContext, reportKey: string) {
    const settingsService = pageContext.getService<ISettingsService>("ISettingsService");

    const userSettings: {
        [key: string]: any;
    } = {};
    userSettings[reportKey + "/" + ReportGuidedTourUtility.teachingBubbleSeenKey] = true;

    await settingsService.setEntries(userSettings, SettingsUserScope.Me);
}

export async function hasUserSeenBubble(pageContext: IVssPageContext, reportKey: string): Promise<boolean> {
    const settingsService = pageContext.getService<ISettingsService>("ISettingsService");

    const userSettings: {
        [key: string]: any;
    } = await settingsService.getEntriesAsync(reportKey, SettingsUserScope.Me);

    return userSettings[ReportGuidedTourUtility.teachingBubbleSeenKey];
}

export function getInfoIcon(titleBubbleCallback: any, toggleTeachingBubble: any, showIcon: boolean, reportTitle: string): JSX.Element | null {
    if (!showIcon) {
        return null;
    }

    return (
        <span ref={titleBubbleCallback}>
            <Button
                className="boards-report-info-icon subtle"
                ariaLabel={format(Resources.InfoIcon, reportTitle)}
                iconProps={{ iconName: "Unknown" }}
                onClick={toggleTeachingBubble}
            />
        </span>
    );
}
